import { API_URL } from "env";
import { mixLogOut } from '../services/mixin';

/**
 * API quản trị Sản phẩm (hàng hóa bán lẻ / mỹ phẩm).
 *  - getProducts:         GET    /api/v1/be/products            (search, status, all, take, page)
 *  - getAllProducts:      GET    /api/v1/be/products?all=1      (không phân trang — dùng cho xuất Excel)
 *  - createProduct:       POST   /api/v1/be/products
 *  - updateProduct:       PUT    /api/v1/be/products/{id}
 *  - updateProductStatus: PATCH  /api/v1/be/products/{id}/status
 *  - deleteProduct:       DELETE /api/v1/be/products/{id}       (soft delete)
 */

/* ================= TYPES ================= */
export interface ProductItem {
    id: number;
    code: string;
    code_2: string | null;
    name: string;
    unit: string | null;
    price: number;
    discount_price: number | null;
    avatar: string | null;
    priority: number;
    /** 0: Đang kinh doanh, 1: Tạm ngưng */
    status: number;
    description: string | null;
    created_at: string;
    updated_at: string;
    deleted_at?: string | null;
}

export interface ProductPayload {
    code: string;
    code_2?: string | null;
    name: string;
    unit?: string;
    price: number;
    discount_price?: number | null;
    avatar?: string | null;
    priority?: number;
    status: number;
    description?: string | null;
}

export interface ProductListParams {
    search?: string;
    status?: number;
    take?: number;
    page?: number;
}

export interface ProductListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: ProductItem[];
    };
    msg: string;
}

export interface ProductMutationResponse {
    status: boolean;
    data?: ProductItem;
    msg: string;
    /** Lỗi validate theo field, vd { code: ["Mã sản phẩm đã tồn tại"] } */
    error?: Record<string, string | string[]>;
}

/* ================= HELPERS ================= */
const authHeaders = (json = false): Record<string, string> => ({
    'Accept': 'application/json',
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
});

const buildQuery = (params?: ProductListParams & { all?: number }) => {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.status !== undefined) query.append('status', String(params.status));
    if (params?.all) query.append('all', String(params.all));
    if (params?.take) query.append('take', String(params.take));
    if (params?.page) query.append('page', String(params.page));
    const qs = query.toString();
    return qs ? `?${qs}` : '';
};

/** Lấy thông báo lỗi đầu tiên trong `error` (validate) để hiển thị toast */
export const firstErrorMessage = (res: { msg?: string; error?: Record<string, string | string[]> }) => {
    const first = res.error ? Object.values(res.error)[0] : undefined;
    const text = Array.isArray(first) ? first[0] : first;
    return text || res.msg || '';
};

/** Gửi request ghi dữ liệu; vẫn đọc body khi HTTP lỗi (422) để lấy msg / error validate */
async function mutate(path: string, method: string, body?: object): Promise<ProductMutationResponse> {
    try {
        const response = await fetch(`${API_URL}${path}`, {
            method,
            headers: authHeaders(!!body),
            body: body ? JSON.stringify(body) : undefined,
        });
        if (response.status === 401) { mixLogOut(); }
        const json = await response.json().catch(() => ({}));
        return {
            status: response.ok && !!json?.status,
            data: json?.data,
            msg: json?.msg || '',
            error: json?.error,
        };
    } catch (error) {
        console.error(`Failed to ${method} ${path}:`, error);
        return { status: false, msg: '' };
    }
}

/* ================= API ================= */
export async function getProducts(params?: ProductListParams): Promise<ProductListResponse> {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/products${buildQuery(params)}`, {
            method: 'GET',
            headers: authHeaders(),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        // Tài liệu ghi data.data (paginator Laravel), API thực tế trả data.items — chuẩn hóa về items
        if (json.status && json.data && Array.isArray(json.data.data)) {
            json.data.items = json.data.data;
            delete json.data.data;
        }
        return json;
    } catch (error) {
        console.error('Failed to fetch products:', error);
        return { status: false, data: { total: 0, current_page: 1, items: [] }, msg: '' };
    }
}

export async function getAllProducts(params?: Omit<ProductListParams, 'take' | 'page'>): Promise<ProductItem[]> {
    try {
        const response = await fetch(`${API_URL}/api/v1/be/products${buildQuery({ ...params, all: 1 })}`, {
            method: 'GET',
            headers: authHeaders(),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        const data = json?.data;
        return Array.isArray(data) ? data : (data?.items || data?.data || []);
    } catch (error) {
        console.error('Failed to fetch all products:', error);
        return [];
    }
}

export const createProduct = (data: ProductPayload) =>
    mutate('/api/v1/be/products', 'POST', data);

export const updateProduct = (id: number, data: ProductPayload) =>
    mutate(`/api/v1/be/products/${id}`, 'PUT', data);

export const updateProductStatus = (id: number, status: number) =>
    mutate(`/api/v1/be/products/${id}/status`, 'PATCH', { status });

export const deleteProduct = (id: number) =>
    mutate(`/api/v1/be/products/${id}`, 'DELETE');
